"use client"

import { createPortal } from "react-dom"
import { Loader2, X } from "lucide-react"
import { Z } from "@/lib/z-index"

interface CompressProgressModalProps {
  /** 压缩进度 0-100 */
  progress: number
  /** 取消按钮回调 */
  onCancel: () => void
}

/**
 * CompressProgressModal — 图片压缩进度弹窗
 *
 * 居中悬浮，显示压缩进度条 + 取消按钮。
 * 遵循 Liquid Glass 设计体系。
 */
export function CompressProgressModal({ progress, onCancel }: CompressProgressModalProps) {
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        style={{ zIndex: Z.MODAL_NESTED }}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_NESTED + 1 }}
      >
        <div className="liquid-glass-panel w-full max-w-xs rounded-2xl p-5 shadow-2xl animate-in zoom-in-95 fade-in duration-200">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">正在压缩图片</p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                压缩完成后自动继续上传
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-2 w-full rounded-full bg-foreground/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${Math.max(progress, 2)}%` }}
            />
          </div>

          {/* Progress text + cancel */}
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-muted-foreground/50 tabular-nums">
              {progress}%
            </span>
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition-colors"
            >
              <X className="h-3 w-3" />
              取消
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
